import { action, makeObservable, observable, runInAction } from "mobx";
import OrderTrackingAnalyticsAPI from "../api/OrderTrackingAnalyticsAPI";
import {
  OrderPipelineRow,
  OrderTrackingAverages,
  OrderTrackingStageSummaryItem,
} from "../models/Dashboard/OrderTracking/OrderTrackingAnalytics";
import {
  defaultOrderTrackingAnalyticsFilters,
  OrderTrackingAnalyticsAPIFilters,
  OrderTrackingAnalyticsFilters,
} from "../models/Dashboard/OrderTracking/OrderTrackingAnalyticsFilters";

const emptyAverages: OrderTrackingAverages = {
  tat_project_approval_hours: 0,
  tat_mockup_approval_hours: 0,
  tat_pr_from_project_hours: 0,
  tat_raw_inward_from_project_hours: 0,
  tat_print_inward_from_project_hours: 0,
  tat_print_inward_from_raw_inward_hours: 0,
  tat_total_project_to_ship_hours: 0,
};

export class OrderTrackingAnalyticsStore {
  orderTrackingAnalyticsAPI: OrderTrackingAnalyticsAPI;
  loading = false;
  filters: OrderTrackingAnalyticsFilters = defaultOrderTrackingAnalyticsFilters();
  stageSummary: OrderTrackingStageSummaryItem[] = [];
  averages: OrderTrackingAverages = emptyAverages;
  pipelineRows: OrderPipelineRow[] = [];
  totalCount = 0;
  totalPages = 0;
  limit = 30;

  constructor() {
    this.orderTrackingAnalyticsAPI = new OrderTrackingAnalyticsAPI();
    makeObservable(this, {
      loading: observable,
      filters: observable,
      stageSummary: observable,
      averages: observable,
      pipelineRows: observable,
      totalCount: observable,
      totalPages: observable,
      limit: observable,
      setLoading: action,
      setFilters: action,
      updateFilters: action,
      resetFilters: action,
      fetchDashboardData: action,
    });
  }

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setFilters = (filters: OrderTrackingAnalyticsFilters) => {
    this.filters = filters;
  };

  updateFilters = (
    updater: (prev: OrderTrackingAnalyticsFilters) => OrderTrackingAnalyticsFilters,
  ) => {
    this.filters = updater(this.filters);
  };

  resetFilters = () => {
    this.filters = defaultOrderTrackingAnalyticsFilters();
  };

  private toApiFilters = (
    filters: OrderTrackingAnalyticsFilters,
  ): OrderTrackingAnalyticsAPIFilters => ({
    start_date: filters.start_date.format("YYYY-MM-DD"),
    end_date: filters.end_date.format("YYYY-MM-DD"),
    order_type_id: filters.order_type_id,
    order_id: filters.order_id,
    sales_poc_id: filters.sales_poc_id,
    pm_poc_id: filters.pm_poc_id,
    customer_id: filters.customer_id,
    stage: filters.stage,
    page_no: filters.page_no,
    limit: filters.limit,
    sort_by: filters.sort_by,
    sort_type: filters.sort_type,
  });

  fetchDashboardData = async (filters: OrderTrackingAnalyticsFilters) => {
    try {
      const apiFilters = this.toApiFilters(filters);
      const [analytics, pipeline] = await Promise.all([
        this.orderTrackingAnalyticsAPI.getAverages(apiFilters),
        this.orderTrackingAnalyticsAPI.getPipeline(apiFilters),
      ]);

      runInAction(() => {
        this.stageSummary = analytics.stage_summary;
        this.averages = analytics.averages;
        this.pipelineRows = pipeline.list;
        this.totalCount = pipeline.total_count;
        this.totalPages = pipeline.total_pages;
        this.limit = pipeline.limit;
        this.filters = filters;
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
}
